import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Heart, Flame, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const PRESET = [101, 251, 501, 1100, 5100];

export default function Events() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [active, setActive] = useState(null);
  const [bucket, setBucket] = useState("seva");
  const [amount, setAmount] = useState(501);
  const [gotra, setGotra] = useState("");
  const [sankalpa, setSankalpa] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([])); }, []);

  const donate = async () => {
    if (!user) { window.location.href = "/login"; return; }
    if (amount <= 0) return toast.error("Enter valid amount");
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${active.id}/donate`, { bucket, amount: Number(amount), gotra, sankalpa_name: sankalpa });
      setReceipt(data);
      toast.success("Contribution recorded");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const closeModal = () => { setActive(null); setReceipt(null); setAmount(501); setBucket("seva"); setGotra(""); setSankalpa(""); };

  const live = events?.filter(e => e.is_live) || [];
  const archived = events?.filter(e => !e.is_live) || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-4xl sm:text-5xl text-[#5C061C]">{t("events.title")}</h1>
        <p className="mt-2 text-[#6E6259] max-w-2xl">{t("events.subtitle")}</p>
      </div>

      <Tabs defaultValue="live">
        <TabsList className="bg-[#F4EEDF]">
          <TabsTrigger value="live" data-testid="events-tab-live">{t("events.live")} ({live.length})</TabsTrigger>
          <TabsTrigger value="archived" data-testid="events-tab-archived">{t("events.archived")} ({archived.length})</TabsTrigger>
        </TabsList>
        {["live","archived"].map((k) => (
          <TabsContent key={k} value={k} className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {(k === "live" ? live : archived).map((e) => (
                <div key={e.id} className="card-warm rounded-2xl overflow-hidden" data-testid={`event-card-${e.id}`}>
                  {e.image_url && <img src={e.image_url} alt={e.title} className="h-48 w-full object-cover" />}
                  <div className="p-5">
                    <div className="flex items-center gap-2">
                      {e.is_live && <span className="pill-live rounded-full px-3 py-1 text-xs font-bold uppercase">● {t("events.live")}</span>}
                    </div>
                    <h3 className="font-serif text-2xl text-[#5C061C] mt-3">{e.title}</h3>
                    <p className="text-[#6E6259] mt-2 line-clamp-3">{e.description}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {e.is_live && (
                        <Button onClick={() => setActive(e)} className="btn-saffron rounded-full h-11 px-5" data-testid={`event-contribute-${e.id}`}>
                          <Heart className="h-4 w-4 mr-2" /> {t("events.contribute")}
                        </Button>
                      )}
                      <Link to={`/events/${e.id}`}>
                        <Button variant="outline" className="rounded-full h-11 px-5 border-[#5C061C] text-[#5C061C]" data-testid={`event-view-${e.id}`}>View gallery</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) closeModal(); }}>
        <DialogContent className="sm:max-w-lg bg-[#FAF7F2] border-[#E8DEC8]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#5C061C]">{active?.title}</DialogTitle>
          </DialogHeader>
          {receipt ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-14 w-14 text-[#15803D] mx-auto" />
              <h3 className="font-serif text-2xl text-[#5C061C] mt-3">Sankalpa accepted</h3>
              <p className="text-[#6E6259] mt-1">Contribution of ₹{receipt.payment.amount} recorded.</p>
              <p className="text-xs text-[#6E6259] mt-2">Receipt: <span className="font-mono">{receipt.receipt_id}</span></p>
              <Button onClick={closeModal} className="btn-maroon rounded-full mt-4 h-11 px-6" data-testid="donation-close-button">{t("common.close")}</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Tabs value={bucket} onValueChange={setBucket}>
                <TabsList className="grid w-full grid-cols-3 bg-[#F4EEDF]">
                  <TabsTrigger value="seva" data-testid="bucket-seva">{t("events.seva")}</TabsTrigger>
                  <TabsTrigger value="puja" data-testid="bucket-puja">{t("events.puja")}</TabsTrigger>
                  <TabsTrigger value="donation" data-testid="bucket-donation">{t("events.donation")}</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="grid grid-cols-5 gap-2">
                {PRESET.map((v) => (
                  <button key={v} onClick={() => setAmount(v)} data-testid={`donation-bucket-${v}`}
                    className={`h-12 rounded-lg border text-sm font-semibold transition ${amount === v ? "bg-[#5C061C] text-white border-[#5C061C]" : "bg-white border-[#E8DEC8] text-[#5C061C] hover:bg-[#F4EEDF]"}`}>
                    ₹{v}
                  </button>
                ))}
              </div>

              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t("events.amount")} className="h-12 bg-white border-[#E8DEC8] text-lg" data-testid="donation-amount-input" />
              <Input value={gotra} onChange={(e) => setGotra(e.target.value)} placeholder={t("events.gotra")} className="bg-white border-[#E8DEC8]" data-testid="donation-gotra-input" />
              <Input value={sankalpa} onChange={(e) => setSankalpa(e.target.value)} placeholder={t("events.sankalpa")} className="bg-white border-[#E8DEC8]" data-testid="donation-sankalpa-input" />

              <Button onClick={donate} disabled={busy} className="btn-saffron w-full h-12 rounded-full text-base" data-testid="donate-now-submit-button">
                <Flame className="h-4 w-4 mr-2" /> {busy ? "Processing..." : `Contribute ₹${amount}`}
              </Button>
              <p className="text-xs text-center text-[#6E6259]">All payments are non-refundable.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
